import React from 'react';
import AppChart, {
  ChartSlice,
  ChartSeries,
} from 'shared/components/Chart/AppChart';
import StatTile from 'shared/components/Dashboard/StatTile';
import MetricCard from 'shared/components/Dashboard/MetricCard';
import DrillBreadcrumb from 'shared/components/Dashboard/DrillBreadcrumb';
import { SchoolDetail } from '../../shared/json/reportDrillData';
import './SchoolDetailView.scss';

export interface SchoolDetailViewProps {
  /** The synthesized detail for the selected school. */
  detail: SchoolDetail;
  /** Block the school belongs to (breadcrumb trail + "home" context). */
  blockName: string;
  /** District (breadcrumb trail + "home" context). */
  districtName: string;
  /** Year shown in the highlighted KPI tile, e.g. "2026-27". */
  year?: string;
  /** Go back to the schools list (clears the selected school). */
  onBack: () => void;
  /**
   * Breadcrumb navigation handlers (optional). When provided, the drill
   * breadcrumb (Tamil Nadu › District › Block › School) can clear each level,
   * keeping the header dropdowns in sync. Falls back to `onBack` when omitted.
   */
  onDistrictChange?: (value: string) => void;
  onBlockChange?: (value: string) => void;
  onSchoolChange?: (value: string) => void;
}

/** Indian short format: 1,08,46,110 → "10.85 L" / "1.22 Cr". */
const formatShort = (n: number): string => {
  if (n >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `${(n / 1e5).toFixed(2)} L`;
  return n.toLocaleString('en-IN');
};

const formatIndian = (n: number): string => n.toLocaleString('en-IN');

/**
 * School-level detail view shown when a user drills Districts → Blocks →
 * Schools and clicks a school row. Follows the same structure as the
 * district/block overview — KPI stat tiles, secondary metrics and the four
 * charts (Management / Gender / Social / Enrolment) — plus a "Back to …
 * Schools" bar and a "School Details" info panel. All data is dummy (seeded).
 */
const SchoolDetailView: React.FC<SchoolDetailViewProps> = ({
  detail,
  blockName,
  districtName,
  year = '2026-27',
  onBack,
  onDistrictChange,
  onBlockChange,
  onSchoolChange,
}) => {
  const { kpi, metrics } = detail;

  // School name with its UDISE appended, for the headings/titles that show the
  // school (the "Name"/"UDISE Code" rows in the info panel keep them separate).
  const nameWithUdise = detail.udiseCode
    ? `${detail.name} (${detail.udiseCode})`
    : detail.name;

  const managementSlices = detail.managementWise as ChartSlice[];
  const genderSlices = detail.genderSplit as ChartSlice[];
  const socialSlices = detail.socialCategory as ChartSlice[];
  const enrolmentSeries: ChartSeries[] = [
    { label: 'Boys', data: detail.enrolmentByGrade.boys, color: '#f0803c' },
    { label: 'Girls', data: detail.enrolmentByGrade.girls, color: '#26a69a' },
  ];

  const fields: { label: string; value: string }[] = [
    { label: 'Name', value: detail.name.toUpperCase() },
    { label: 'UDISE Code', value: detail.udiseCode },
    { label: 'Management', value: detail.management },
    { label: 'Category', value: detail.category },
    { label: 'School Type', value: detail.schoolType },
    { label: 'Level', value: detail.level },
    { label: 'Total Staff', value: String(detail.totalStaff) },
    { label: 'Non-Teaching', value: String(detail.nonTeaching) },
  ];

  return (
    <div className="school-detail">
      {/* ── KPI stat tiles ── */}
      <div className="row g-3 mb-3">
        <div className="col-6 col-md-4 col-xl">
          <StatTile
            icon="pi pi-building"
            value={formatIndian(kpi.schools)}
            subValue={formatIndian(kpi.schools)}
            label="Total Schools"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <StatTile
            icon="pi pi-users"
            value={formatShort(kpi.students)}
            subValue={formatIndian(kpi.students)}
            label="Total Students"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <StatTile
            icon="pi pi-user"
            value={formatShort(kpi.boys)}
            subValue={formatIndian(kpi.boys)}
            label="Boys"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <StatTile
            icon="pi pi-user"
            value={formatShort(kpi.girls)}
            subValue={formatIndian(kpi.girls)}
            label="Girls"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <StatTile
            icon="pi pi-briefcase"
            value={formatIndian(kpi.teachers)}
            subValue={formatIndian(kpi.teachers)}
            label="Teachers"
          />
        </div>
        <div className="col-6 col-md-4 col-xl">
          <StatTile
            variant="solid"
            value={nameWithUdise}
            label={`${year} · School Level`}
            note="Source: EMIS"
          />
        </div>
      </div>

      {/* ── Secondary metrics ── */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-lg-3">
          <MetricCard
            value={metrics.gpi}
            label="Gender Parity Index"
            accent="#22c55e"
          />
        </div>
        <div className="col-6 col-lg-3">
          <MetricCard value={metrics.ptr} label="Pupil-Teacher Ratio" />
        </div>
        <div className="col-6 col-lg-3">
          <MetricCard
            value={metrics.girlsShare}
            label="Girls Share"
            accent="#f0803c"
          />
        </div>
        <div className="col-6 col-lg-3">
          <MetricCard
            value={metrics.avgStudents}
            label="Avg Students / School"
          />
        </div>
      </div>

      {/* ── Four overview charts ── */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-6 col-xl-3">
          <AppChart
            type="doughnut"
            title={`Management-Wise — ${nameWithUdise}`}
            icon="pi pi-chart-pie"
            iconColor="#2f5fb0"
            slices={managementSlices}
            height={320}
          />
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <AppChart
            type="doughnut"
            title="Gender Split"
            icon="pi pi-users"
            iconColor="#7e57c2"
            slices={genderSlices}
            height={320}
          />
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <AppChart
            type="doughnut"
            title="Social Category"
            icon="pi pi-th-large"
            iconColor="#26a69a"
            slices={socialSlices}
            height={320}
          />
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <AppChart
            type="stackedBar"
            title="Enrolment by Grade"
            icon="pi pi-chart-bar"
            iconColor="#f0803c"
            labels={detail.enrolmentByGrade.grades}
            series={enrolmentSeries}
            yTitle="Students"
            height={320}
          />
        </div>
      </div>

      {/* ── Drill breadcrumb (Tamil Nadu › District › Block › School) ── */}
      <DrillBreadcrumb
        district={districtName || null}
        block={blockName || null}
        school={nameWithUdise}
        onBack={onBack}
        onDistrictChange={onDistrictChange}
        onBlockChange={onBlockChange}
        onSchoolChange={onSchoolChange}
        className="school-detail__crumbs"
      />

      {/* ── School Details panel (original grid UI) ── */}
      <div className="school-detail__panel">
        <div className="school-detail__panel-head">
          <i className="pi pi-info-circle" />
          <span>School Details</span>
        </div>
        <div className="school-detail__grid">
          {fields.map((f) => (
            <div className="school-detail__field" key={f.label}>
              <span className="school-detail__field-label">{f.label}</span>
              <span className="school-detail__field-value">{f.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SchoolDetailView;
